import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { HART_BASE_URL } from '../lib/defaults'
import '../orders.css'

/* ── constants ───────────────────────────────────────────────── */
const CATS = {
  timber:    { c: 'var(--c-timber)',    label: 'Timber' },
  fixings:   { c: 'var(--c-fixings)',   label: 'Fixings & Hardware' },
  adhesives: { c: 'var(--c-adhesives)', label: 'Adhesives, Sealants & Finishes' },
  site:      { c: 'var(--c-site)',      label: 'Site Materials' },
  additional:{ c: 'var(--c-additional)',label: 'Additional' },
}
const STATUSES = [
  { id: 'to_order',   label: 'To order',     color: 'var(--st-toorder)'  },
  { id: 'enquiry',    label: 'Enquiry sent', color: 'var(--st-enquiry)'  },
  { id: 'ordered',    label: 'Ordered',      color: 'var(--st-ordered)'  },
  { id: 'from_stock', label: 'From stock',   color: 'var(--c-site)'      },
  { id: 'delivered',  label: 'Delivered',    color: 'var(--st-delivered)'},
]
const STATUS_RANK = { to_order: 0, enquiry: 1, ordered: 2, from_stock: 3, delivered: 4 }
const CAT_ORDER   = ['timber','fixings','adhesives','site','additional']
const SUPP_PALETTE= ['#6E8453','#5B7F8C','#B58A3E','#9A8B74','#B06548','#7A6F9C','#4F7A6A','#A65D5D']

/* ── helpers ─────────────────────────────────────────────────── */
const gbp  = n => '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const qty  = n => n.toLocaleString('en-GB', { maximumFractionDigits: 3 })

function supplierColor(name) {
  if (!name) return 'var(--ink-3)'
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return SUPP_PALETTE[h % SUPP_PALETTE.length]
}
function initials(name) {
  const w = (name || '').replace(/[–—-]/g, ' ').split(/\s+/).filter(Boolean)
  return w.length >= 2 ? (w[0][0] + w[1][0]).toUpperCase() : (name || '?').slice(0, 2).toUpperCase()
}
function inferSupplier(description, isHart) {
  if (isHart) return 'Hart Wholesale'
  const m = description.match(/\s[–—]\s*([^–—]{2,30})$/)
  return m ? m[1].trim() : null
}

function buildGroups(lines, groupBy, projectsMap) {
  const merged = new Map()
  for (const ln of lines) {
    const k = ln.name + '|' + (ln.supplier || '—') + '|' + ln.cat
    if (!merged.has(k)) merged.set(k, { ...ln, key: k, qty: 0, sources: [] })
    const m = merged.get(k)
    m.qty += ln.qty
    m.sources.push({ project: ln.project, qty: ln.qty })
    if (STATUS_RANK[ln.status] < STATUS_RANK[m.status]) m.status = ln.status
  }
  const items = [...merged.values()]

  const groups = new Map()
  const keyOf = ln => groupBy === 'supplier' ? (ln.supplier || '—')
    : groupBy === 'category' ? ln.cat
    : (ln.sources[0]?.project || 'unknown')
  for (const ln of items) {
    const k = keyOf(ln)
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k).push(ln)
  }

  return [...groups.entries()].map(([k, ls]) => {
    const subtotal = ls.reduce((s, l) => s + (l.unit || 0) * l.qty, 0)
    const counts = {}; ls.forEach(l => counts[l.status] = (counts[l.status] || 0) + 1)
    let name = k, color
    if (groupBy === 'supplier')  name = k === '—' ? 'Unassigned supplier' : k
    if (groupBy === 'category') { name = CATS[k]?.label || k; color = CATS[k]?.c }
    if (groupBy === 'project')   name = projectsMap[k] || k
    return { key: k, name, color, lines: ls, subtotal, counts }
  }).sort((a, b) => {
    if (groupBy === 'supplier')  return a.key === '—' ? 1 : b.key === '—' ? -1 : b.subtotal - a.subtotal
    if (groupBy === 'category')  return CAT_ORDER.indexOf(a.key) - CAT_ORDER.indexOf(b.key)
    return a.key.localeCompare(b.key)
  })
}

/* ── icons ───────────────────────────────────────────────────── */
const Ic = {
  search:   p => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>,
  plus:     p => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" {...p}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  x:        p => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" {...p}><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>,
  check:    p => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="20 6 9 17 4 12"/></svg>,
  chev:     p => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="6 9 12 15 18 9"/></svg>,
  doc:      p => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><polyline points="14 3 14 8 19 8"/></svg>,
  dots:     p => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" {...p}><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>,
  link:     p => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
}

/* ── outside-click hook ──────────────────────────────────────── */
function useOutside(open, close) {
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = e => { if (ref.current && !ref.current.contains(e.target)) close() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open, close])
  return ref
}

/* ── Cbx ─────────────────────────────────────────────────────── */
function Cbx({ on, onClick }) {
  return (
    <button className={'cbx' + (on ? ' on' : '')} onClick={onClick}>
      {on && <Ic.check />}
    </button>
  )
}

/* ── StatusChip ──────────────────────────────────────────────── */
function StatusChip({ status, onPick }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, right: 0 })
  const close = React.useCallback(() => setOpen(false), [])
  const ref = useOutside(open, close)
  const btnRef = useRef(null)
  const s = STATUSES.find(s => s.id === status) || STATUSES[0]
  const handleOpen = e => {
    e.stopPropagation()
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right })
    }
    setOpen(o => !o)
  }
  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <button ref={btnRef} className="stchip" data-s={status} onClick={handleOpen}>
        <span className="sd" />{s.label}
      </button>
      {open && (
        <div className="pop" style={{ position: 'fixed', top: pos.top, right: pos.right }}>
          {STATUSES.map(st => (
            <button key={st.id} onClick={() => { onPick(st.id); setOpen(false) }}>
              <span className="sd" style={{ background: st.color }} />{st.label}
            </button>
          ))}
        </div>
      )}
    </span>
  )
}

/* ── ScopeBar ────────────────────────────────────────────────── */
function ScopeBar({ projects, scope, liveOnly, onToggleLive, onAdd, onRemove }) {
  const [open, setOpen] = useState(false)
  const close = React.useCallback(() => setOpen(false), [])
  const ref = useOutside(open, close)
  const inScope    = projects.filter(p => scope.includes(p.id))
  const candidates = projects.filter(p => !scope.includes(p.id) && (!liveOnly || p.status === 'live'))
  return (
    <div className="scope">
      <div className="scope-top">
        <span className="scope-l">Projects in scope</span>
        <div style={{ flex: 1 }} />
        <div className="seg">
          <button className={liveOnly ? 'on' : ''} onClick={() => onToggleLive(true)}>Live</button>
          <button className={!liveOnly ? 'on' : ''} onClick={() => onToggleLive(false)}>All</button>
        </div>
      </div>
      <div className="scope-chips">
        {inScope.map(p => (
          <span key={p.id} className="pchip">
            {p.proposal_number && <span className="ref">{p.proposal_number}</span>}
            {p.name}
            <button className="x" onClick={() => onRemove(p.id)}><Ic.x /></button>
          </span>
        ))}
        <span ref={ref} style={{ position: 'relative' }}>
          <button className="add-proj" onClick={() => setOpen(o => !o)}><Ic.plus /> Add project</button>
          {open && (
            <div className="menu">
              <div className="menu-top"><span className="ml">Add to scope</span></div>
              {candidates.length === 0 && <div className="menu-item dis"><span className="nm">No more projects</span></div>}
              {candidates.map(p => (
                <div key={p.id} className="menu-item"
                  onClick={() => { onAdd(p.id); setOpen(false) }}>
                  {p.proposal_number && <span className="ref">{p.proposal_number}</span>}
                  <span className="nm">{p.name}</span>
                  {p.status && (
                    <span className={'badge ' + (p.status === 'live' ? 'live' : 'enquiry')}>{p.status}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </span>
      </div>
    </div>
  )
}

/* ── Toolbar ─────────────────────────────────────────────────── */
function Toolbar({ groupBy, onSetGroup, query, onQuery }) {
  return (
    <div className="toolbar">
      <span className="lbl">Group by</span>
      <div className="seg">
        {[['supplier','Supplier'],['category','Category'],['project','Project']].map(([id, lbl]) => (
          <button key={id} className={groupBy === id ? 'on' : ''} onClick={() => onSetGroup(id)}>{lbl}</button>
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <div className="search">
        <Ic.search />
        <input placeholder="Search items…" value={query} onChange={e => onQuery(e.target.value)} />
      </div>
    </div>
  )
}

/* ── BulkBar ─────────────────────────────────────────────────── */
function BulkBar({ n, onMark, onClear }) {
  return (
    <div className="bulk">
      <span className="count"><b>{n}</b> selected</span>
      <div style={{ flex: 1 }} />
      <button className="primary" onClick={() => onMark('ordered')}><Ic.doc /> Mark ordered</button>
      <button onClick={() => onMark('enquiry')}>Set enquiry</button>
      <button onClick={() => onMark('to_order')}>Reset to order</button>
      <button onClick={onClear}><Ic.x /> Clear</button>
    </div>
  )
}

/* ── OrderLine ───────────────────────────────────────────────── */
function OrderLine({ line, groupBy, selected, expanded, projectsMap, onSel, onStatus, onExpand }) {
  const total = line.unit == null ? null : line.unit * line.qty
  let tag
  if (groupBy === 'category') {
    tag = <span className="tag">{line.supplier || <span style={{ color: 'var(--ink-3)' }}>No supplier</span>}</span>
  } else {
    const c = CATS[line.cat]
    tag = c
      ? <span className="tag"><span className="td" style={{ background: c.c }} />{c.label.split(',')[0]}</span>
      : <span className="tag">{line.cat}</span>
  }
  const multi = line.sources && line.sources.length > 1
  return (
    <>
      <div className={'oline' + (selected ? ' sel' : '')}>
        <Cbx on={selected} onClick={() => onSel(line.key)} />
        <div style={{ minWidth: 0 }}>
          <div className="ol-name" title={line.name}>{line.name}</div>
          {multi
            ? <div className="ol-src" onClick={() => onExpand(line.key)}>
                {line.sources.length} projects · {expanded ? 'hide' : 'show'}
              </div>
            : groupBy !== 'project' && line.sources?.[0]
              ? <div className="ol-src" style={{ cursor: 'default' }}>{projectsMap[line.sources[0].project] || ''}</div>
              : null}
        </div>
        {tag}
        <div className="num tnum">{qty(line.qty)}</div>
        <div className="num tnum">{line.unit == null ? '—' : gbp(line.unit)}</div>
        {total == null
          ? <div className="needprice">Get price</div>
          : <div className="num tot tnum">{gbp(total)}</div>}
        <StatusChip status={line.status} onPick={s => onStatus(line.key, s)} />
        <div className="ol-act"><button title="More"><Ic.dots /></button></div>
      </div>
      {multi && expanded && (
        <div className="src-break">
          {line.sources.map(s => (
            <div className="src-row" key={s.project + s.qty}>
              <span className="ref">{s.project.slice(0, 6)}</span>
              {projectsMap[s.project] || s.project}
              <span style={{ marginLeft: 'auto' }} className="tnum">{qty(s.qty)} × {gbp(line.unit || 0)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

/* ── GroupCard ───────────────────────────────────────────────── */
function GroupCard({ group, groupBy, children }) {
  const { counts } = group
  const total  = group.lines.length
  const outstanding = (counts.to_order || 0) + (counts.enquiry || 0)
  const segs = [
    { k: 'delivered', c: 'var(--st-delivered)' },
    { k: 'ordered',   c: 'var(--st-ordered)'   },
    { k: 'enquiry',   c: 'var(--st-enquiry)'   },
    { k: 'to_order',  c: 'var(--st-toorder)'   },
  ]
  let icon
  if (groupBy === 'supplier') {
    const col = group.key === '—' ? 'var(--ink-3)' : supplierColor(group.name)
    icon = group.key === '—'
      ? <span className="gc-ic" style={{ background: 'var(--surface-3)', color: 'var(--ink-3)', fontSize: 14 }}>?</span>
      : <span className="gc-ic" style={{ background: col }}>{initials(group.name)}</span>
  } else if (groupBy === 'category') {
    icon = <span className="gc-dot" style={{ '--gc': group.color }} />
  } else {
    icon = <span className="gc-ic" style={{ background: 'var(--header)', color: 'var(--cream)', fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 11 }}>{initials(group.name)}</span>
  }
  const accent = groupBy === 'category' ? group.color
    : group.key === '—' ? 'var(--ink-3)'
    : groupBy === 'supplier' ? supplierColor(group.name)
    : 'var(--header)'
  return (
    <section className="gcard" style={{ '--gc': accent }}>
      <div className="gcard-head">
        {icon}
        <div style={{ minWidth: 0 }}>
          <div className="gc-name">{group.name}</div>
          <div className="gc-meta">
            {total} line{total !== 1 ? 's' : ''}
            {outstanding ? ` · ${outstanding} to order` : ' · all ordered or delivered'}
          </div>
        </div>
        <div className="gc-right">
          <span className="gc-sub tnum">{gbp(group.subtotal)}</span>
        </div>
      </div>
      <div className="gc-prog">
        {segs.map(s => { const w = (counts[s.k] || 0) / total * 100; return w ? <i key={s.k} style={{ width: w + '%', background: s.c }} /> : null })}
      </div>
      <div className="oline-head">
        <span /><span>Description</span>
        <span>{groupBy === 'category' ? 'Supplier' : 'Category'}</span>
        <span className="r">Qty</span><span className="r">Unit</span><span className="r">Total</span>
        <span>Status</span><span />
      </div>
      {children}
    </section>
  )
}

/* ── SummaryRail ─────────────────────────────────────────────── */
function SummaryRail({ stats, buckets, total, hartCartUrl }) {
  const pct = v => total ? v / total * 100 : 0
  return (
    <div className="srail">
      <div className="sr-title">Procurement Summary</div>
      <div className="sr-scope">
        <div className="sr-stat"><div className="n tnum">{stats.projects}</div><div className="l">Projects</div></div>
        <div className="sr-stat"><div className="n tnum">{stats.suppliers}</div><div className="l">Suppliers</div></div>
        <div className="sr-stat"><div className="n tnum">{stats.lines}</div><div className="l">Lines</div></div>
      </div>
      <div className="sr-group">
        <div className="sr-bar">
          {[['delivered','var(--st-delivered)'],['ordered','var(--st-ordered)'],['enquiry','var(--st-enquiry)'],['to_order','var(--st-toorder)']]
            .map(([k, c]) => { const w = pct(buckets[k] || 0); return w ? <i key={k} style={{ width: w + '%', background: c }} /> : null })}
        </div>
        {STATUSES.map(s => (
          <div key={s.id} className="sr-row">
            <span className="sd" style={{ background: s.color }} />
            <span className="k">{s.label}</span>
            <span className="v tnum">{gbp(buckets[s.id] || 0)}</span>
          </div>
        ))}
        <div className="sr-row tot">
          <span className="k">Total value</span>
          <span className="v tnum">{gbp(total)}</span>
        </div>
      </div>
      {hartCartUrl && (
        <div className="sr-actions">
          <a href={hartCartUrl} target="_blank" rel="noopener noreferrer"
            className="sr-cta primary" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Ic.link /> Order Hart items ↗
          </a>
        </div>
      )}
    </div>
  )
}

/* ── Toast ───────────────────────────────────────────────────── */
function Toast({ show, msg }) {
  return (
    <div className={'toast' + (show ? ' show' : '')}>
      <span className="ok"><Ic.check /></span> {msg}
    </div>
  )
}

/* ── Main component ──────────────────────────────────────────── */
export default function OrdersScreen({ session }) {
  const [projects,     setProjects]     = useState([])
  const [rawLines,     setRawLines]     = useState([])
  const [scope,        setScope]        = useState([])
  const [liveOnly,     setLiveOnly]     = useState(true)
  const [groupBy,      setGroupBy]      = useState('supplier')
  const [query,        setQuery]        = useState('')
  const [expanded,     setExpanded]     = useState({})
  const [sel,          setSel]          = useState(new Set())
  const [statuses,     setStatuses]     = useState({})
  const [toast,        setToast]        = useState({ show: false, msg: '' })
  const [loading,      setLoading]      = useState(true)
  const [loadingItems, setLoadingItems] = useState(false)

  /* load projects once */
  useEffect(() => {
    supabase.from('projects')
      .select('id, name, status, proposal_number')
      .eq('user_id', session.user.id)
      .eq('is_template', false)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        const projs = data || []
        setProjects(projs)
        setScope(projs.filter(p => p.status === 'live').map(p => p.id))
        setLoading(false)
      })
  }, [session.user.id])

  /* reload items when scope changes */
  useEffect(() => {
    if (loading) return
    if (scope.length === 0) { setRawLines([]); return }
    setLoadingItems(true)
    supabase.from('line_items').select('*').in('project_id', scope)
      .then(({ data }) => {
        const out = []
        for (const row of (data || [])) {
          if (row.category === 'labour') continue

          if (row.hart_basket) {
            let basket = []; try { basket = JSON.parse(row.hart_basket) } catch { continue }
            for (const b of basket) out.push({
              project: row.project_id,
              cat: 'fixings',
              name: b.name,
              supplier: 'Hart Wholesale',
              unit: b.price ?? null,
              qty: b.qty ?? 1,
              status: 'to_order',
              is_hart: true,
              variantId: b.variantId || '',
            })
            continue
          }

          if (!row.description || !row.quantity || row.quantity <= 0) continue
          out.push({
            project: row.project_id,
            cat: row.category || 'additional',
            name: row.description,
            supplier: inferSupplier(row.description, false),
            unit: row.unit_cost ?? null,
            qty: row.quantity,
            status: 'to_order',
            is_hart: false,
            variantId: '',
          })
        }
        setRawLines(out)
        setLoadingItems(false)
      })
  }, [scope, loading])

  /* project display-name map */
  const projectsMap = Object.fromEntries(
    projects.map(p => [p.id, p.proposal_number ? `${p.proposal_number} · ${p.name}` : p.name])
  )

  /* build groups (applying status overrides) */
  const linesWithStatus = rawLines.map(l => {
    const k = l.name + '|' + (l.supplier || '—') + '|' + l.cat
    const ov = statuses[k]
    return ov ? { ...l, status: ov.status } : l
  })

  let groups = buildGroups(linesWithStatus, groupBy, projectsMap)
  /* re-apply overrides to the merged keys so StatusChip sees them */
  groups = groups.map(g => ({
    ...g,
    lines: g.lines.map(l => statuses[l.key] ? { ...l, ...statuses[l.key] } : l),
  }))
  groups.forEach(g => {
    const c = {}; g.lines.forEach(l => c[l.status] = (c[l.status] || 0) + 1); g.counts = c
  })

  if (query.trim()) {
    const q = query.toLowerCase()
    groups = groups
      .map(g => ({ ...g, lines: g.lines.filter(l => l.name.toLowerCase().includes(q) || (l.supplier || '').toLowerCase().includes(q)) }))
      .filter(g => g.lines.length)
  }

  /* summary stats */
  const allMerged  = buildGroups(linesWithStatus, groupBy, projectsMap).flatMap(g => g.lines)
  const buckets    = { to_order: 0, enquiry: 0, ordered: 0, delivered: 0 }
  allMerged.forEach(l => { const k = statuses[l.key]?.status || l.status; buckets[k] = (buckets[k] || 0) + (l.unit || 0) * l.qty })
  const grandTotal = Object.values(buckets).reduce((a, b) => a + b, 0)
  const supplierSet = new Set(rawLines.map(l => l.supplier).filter(Boolean))
  const stats = { projects: scope.length, suppliers: supplierSet.size, lines: allMerged.length }

  /* hart cart URL (unordered hart items) */
  const hartCartUrl = (() => {
    const items = rawLines.filter(l => l.is_hart && l.variantId)
    const unordered = items.filter(l => {
      const k = l.name + '|' + (l.supplier || '—') + '|' + l.cat
      const s = statuses[k]?.status || 'to_order'
      return s === 'to_order' || s === 'enquiry'
    })
    if (!unordered.length) return null
    const parts = unordered.map(l => `${l.variantId}:${Math.ceil(l.qty)}`).join(',')
    return `${HART_BASE_URL}/cart/${parts}?storefront=true`
  })()

  /* actions */
  const setStatus  = (key, status) => setStatuses(p => ({ ...p, [key]: { status } }))
  const toggleSel  = key => setSel(p => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n })
  const bulkMark   = status => {
    setStatuses(p => { const n = { ...p }; sel.forEach(k => n[k] = { status }); return n })
    const verb = status === 'ordered' ? 'marked ordered' : status === 'enquiry' ? 'set to enquiry' : 'reset to order'
    flash(`${sel.size} line${sel.size !== 1 ? 's' : ''} ${verb}`)
    setSel(new Set())
  }
  const flash = msg => {
    setToast({ show: true, msg })
    clearTimeout(window.__akkOrders)
    window.__akkOrders = setTimeout(() => setToast(t => ({ ...t, show: false })), 2100)
  }

  if (loading) return (
    <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--ink-3)', fontSize: '13px', fontFamily: 'var(--font)' }}>
      Loading…
    </div>
  )

  return (
    <div className="ow">
      <main>
        <div className="page-head">
          <h1 className="page-title">Orders</h1>
          <span className="page-sub">Materials to buy across your live projects</span>
        </div>

        <ScopeBar
          projects={projects} scope={scope} liveOnly={liveOnly}
          onToggleLive={setLiveOnly}
          onAdd={id => setScope(s => [...s, id])}
          onRemove={id => setScope(s => s.filter(x => x !== id))}
        />

        <Toolbar groupBy={groupBy} onSetGroup={setGroupBy} query={query} onQuery={setQuery} />

        {sel.size > 0 && <BulkBar n={sel.size} onMark={bulkMark} onClear={() => setSel(new Set())} />}

        {loadingItems && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ink-3)', fontSize: '13px' }}>Loading…</div>
        )}

        {!loadingItems && scope.length === 0 && (
          <div className="gcard"><div className="empty">No projects in scope. Add a project above to start buying.</div></div>
        )}

        {!loadingItems && scope.length > 0 && groups.length === 0 && (
          <div className="gcard"><div className="empty">No items found{query ? ' matching your search' : ' on selected projects'}.</div></div>
        )}

        {!loadingItems && groups.map(g => (
          <GroupCard key={groupBy + g.key} group={g} groupBy={groupBy}>
            {g.lines.map(line => (
              <OrderLine key={line.key} line={line} groupBy={groupBy}
                selected={sel.has(line.key)} expanded={!!expanded[line.key]}
                projectsMap={projectsMap}
                onSel={toggleSel} onStatus={setStatus}
                onExpand={k => setExpanded(e => ({ ...e, [k]: !e[k] }))} />
            ))}
          </GroupCard>
        ))}
      </main>

      <aside className="rail-col">
        <SummaryRail
          stats={stats} buckets={buckets} total={grandTotal}
          hartCartUrl={hartCartUrl}
        />
      </aside>

      <Toast show={toast.show} msg={toast.msg} />
    </div>
  )
}
